<!--
  Component: VizFourBarRating

  This component creates an svg visualization for a four bar rating.  The
  rating is also mapped to a screen-reader label for accessibility.

  Notes:
  - Shows 0 bars while loading (rating=undefined)
  - Blank ratings show nothing, or noRatingText, if latter exists
  - Parent component can override fill styling by using /deep/ scss rules if
      an additional class is added to root element (to be more specific)
  - There is a 'bg' element to allow setting a printable background color

  Props:
  - rating - 1 to 4 value of rating to be displayed, or string to be displayed
      in lieu of bars.  Not really optional (should be connected), but no
      value yet (undefined) will display as 0 bars
  - xPadding, yPadding - (optional) padding in pixels between/around bars
  - noRatingText - (optional) default text to be displayed if there is no value
      or string in the rating prop (blank/missing/null)
  - noRatingTextClass - (optional) class to add to no rating text element
-->

<template>
  <!--
  Use aria-label because title changes aren't capture automatically by NVDA
  Not using aria-labelledby because then we would need unique id passed in from outside

  Another alternative is to use aria-hidden='true', with sr-only span:
  https://css-tricks.com/gotchas-getting-inline-svg-production-part-ii/#article-header-id-9
   -->
  <div :class="['viz-four-bar-rating',{'no-rating':isNoRating, 'hollow-style':hollowStyle}]">
    <svg ref="root"
      role="img"
      focusable="false"
      :aria-label="ratingTitle">
      <title>{{ ratingTitle }}</title>
      <rect class="bg" :width="inner.w" :height="inner.h"/>
      <rect v-for="n in numBars"
        :key="n"
        :class="barClasses(n)"
        :x="barPositionX(n)"
        :y="padding.y"
        :width="barSize.w"
        :height="barSize.h"
      />
      <rect v-for="n in hollowBarNum"
        :key="`hollow-${n}`"
        class="bar hollow"
        :x="barPositionX(hollowBarOffset+n)+hollowBarBorderWidth"
        :y="padding.y+hollowBarBorderWidth"
        :width="hollowBarSize.w"
        :height="hollowBarSize.h"
      />
    </svg>
    <div v-if="isNoRating" :class="['v-outer no-rating-text',noRatingTextClass]">
      <div class="v-middle content">{{noRatingDisplayText}}</div>
    </div>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component'

import { valIsMissing } from '@/utils/data'
import { RootSizeMixin } from '@/mixin'

const NUM_BARS = 4
const defaultPadding = { x: 2, y: 1 }
const ratingDescriptions = [
  'Needs Improvement',
  'Fair',
  'Good',
  'Excellent'
]

function isLoadingRating (r) {
  return (r === undefined)
}

function isRatingEmpty (r) {
  return isLoadingRating(r) || valIsMissing(r)
}

function isRatingValid (r) {
  if (isRatingEmpty(r)) return true
  // Use +r to convert strings to numbers
  return (Number.isInteger(+r) && +r >= 1 && +r <= NUM_BARS)
}

export function descriptionForRating (r) {
  if (isRatingEmpty(r)) return ''
  if (!isRatingValid(r)) return r
  return ratingDescriptions[r - 1]
}

@Component({
  props: {
    xPadding: Number,
    yPadding: Number,
    hollowStyle: {type: Boolean, default: false},
    rating: [Number, String],
    noRatingText: String,
    noRatingTextClass: String
  }
})
export default class VizFourBarRating extends mixins(RootSizeMixin) {
  numBars = NUM_BARS

  /* Getters */

  get isLoading () { return isLoadingRating(this.rating) }
  get isRatingEmpty () { return isRatingEmpty(this.rating) }
  get isRatingValid () { return isRatingValid(this.rating) }
  get isNoRating () {
    return !this.isLoading && (this.isRatingEmpty || !this.isRatingValid)
  }

  get ratingTitle () {
    if (this.isRatingEmpty) return ''
    if (!this.isRatingValid) return this.rating
    return `Rating: ${this.rating} of ${this.numBars} Bars, ${ratingDescriptions[this.rating - 1]}`
  }

  get padding () {
    const p = Object.assign({}, defaultPadding)
    if (this.xPadding !== undefined) p.x = this.xPadding
    if (this.yPadding !== undefined) p.y = this.yPadding
    return p
  }

  get barSize () {
    const xPaddingNum = this.hollowStyle
      ? this.numBars - 1
      : this.numBars + 1
    return {
      w: Math.max(0, (this.inner.w - xPaddingNum * this.padding.x) / this.numBars),
      h: Math.max(0, this.inner.h - 2 * this.padding.y)
    }
  }

  get hollowBarBorderWidth () {
    // design gives two size: 10 for small, 17 for big, so 13 is approximately the middle
    return this.barSize.h > 13 ? 2 : 1
  }

  get hollowBarSize () {
    return {
      w: Math.max(0, this.barSize.w - this.hollowBarBorderWidth * 2),
      h: Math.max(0, this.barSize.h - this.hollowBarBorderWidth * 2)
    }
  }

  get noRatingDisplayText () {
    // Use value from data first, otherwise default to noRatingText prop
    return (!this.isRatingEmpty) ? this.rating
      : (this.noRatingText !== undefined) ? this.noRatingText : ''
  }

  get hollowBarOffset () {
    return this.isRatingValid && !this.isRatingEmpty
      ? +this.rating
      : 0
  }

  get hollowBarNum () {
    return this.hollowStyle
      ? this.numBars - this.hollowBarOffset
      : 0
  }

  /* Methods */

  barClasses (n) {
    const c = ['bar']
    if (this.isRatingValid && n <= this.rating) c.push('filled')
    return c
  }

  barPositionX (n) {
    const offset = this.hollowStyle ? 0 : this.padding.x
    return offset + (n - 1) * (this.padding.x + this.barSize.w)
  }
}
</script>

<!-- These are base-version formatting rules meant to be overridden, so keep them unscoped -->
<style lang="scss">
.viz-four-bar-rating {
  display: inline-block;
  position: relative;
  svg {
    vertical-align: middle;
    box-sizing: border-box;
    border-width: 1px;
    border-style: solid;
    width: 100%;
    height: 100%;
  }
  &.no-rating {
    /* Keep svg on page isntead of display:none; so sizing can still be applied
       on layout changes  */
    svg {
      visibility: hidden;
    }
    .no-rating-text {
      position: absolute;
      top: 0;
      text-align: center;
    }
  }
  &.large {
    width: 104px;
    height: 21px;
  }
  &.medium {
    width: 73px;
    height: 15px;
  }
  &.small {
    width: 64px;
    height: 12px;
  }
  .bg         { fill: white; }
  .bar        { fill: #C4C4C4; }
  .bar.filled { fill: black; }
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.viz-four-bar-rating {
  &.hollow-style {
    .bg  {fill: none;}
    .bar {
      fill: black;
    }
    .bar.hollow {
      fill: white;
    }
    &.large {
      width: 77px;
      height: 17px;
    }
    &.small {
      width: 49px;
      height: 10px
    }
    svg {
      border-width: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/VizFourBarRating.vue